import { type MouseEvent, useEffect, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { useRafLoop } from 'react-use';
import { useStore, useStoreApi, useReactFlow } from 'reactflow';
import { ZoomBehavior, zoomIdentity } from 'd3-zoom';
import { interpolate as interpolateLinearly } from 'd3-interpolate';
import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import { getMouseX, getMouseY } from 'components/DragAndDropLayout/helpers';
import { getExtent } from './layout';
import { clamp } from 'utility/snippets';
import {
  CANVAS_PADDING,
  CANVAS_TOP_PADDING,
  RECENTER_TRANSITION_MS,
  REFRESH_PX,
} from './conatants';
import {
  getPlacing,
  getPlacingVariable,
} from 'pages/AutomationEngine/store/selectors';
import { STEP_SLOT_HEIGHT } from '../Steps/constants';
import { type StepSlotHeight } from './types';

type Coord = { x: number; y: number };

const nearLeft = ({ x }: Coord, { left }: DOMRect, delta = 100) => {
  return Math.abs(left - x) < delta;
};

const nearRight = ({ x }: Coord, { right }: DOMRect, delta = 100) => {
  return Math.abs(right - x) < delta;
};

const nearTop = ({ y }: Coord, { top }: DOMRect, delta = 100) => {
  return Math.abs(top - y) < delta;
};

const nearBottom = ({ y }: Coord, { bottom }: DOMRect, delta = 100) => {
  return Math.abs(bottom - y) < delta;
};

export function useScrollFlowWhilePlacingCore(placing: boolean) {
  const transform = useStore((state) => state.transform);

  const d3Zoom = useStore((s) => s.d3Zoom);
  const d3Selection = useStore((s) => s.d3Selection);
  const stateRef = useRef<{
    dX: number;
    dY: number;
    nativeEvent: MouseEvent['nativeEvent'] | null;
    flowElRect: DOMRect | null;
    mouseOnFlow: boolean;
    hasInteractedWithoutScroll: boolean;
  }>({
    dX: 0,
    dY: 0,
    nativeEvent: null,
    flowElRect: null,
    mouseOnFlow: false,
    hasInteractedWithoutScroll: false,
  });

  // This interval is the loop that regularly reads dX and dY,
  // and uses it to continuous scroll by dX and dY pixels.
  const [stopScrolling, startScrolling] = useRafLoop(() => {
    // Reset on an interval, allows us to recompute less often but keep it up to date
    stateRef.current.flowElRect = null;
    const { dX, dY } = stateRef.current;
    if ((dX !== 0 || dY !== 0) && d3Zoom && d3Selection) {
      d3Zoom.translateBy(d3Selection, dX, dY);
    }
  });

  useEffect(() => {
    if (placing) {
      startScrolling();
    } else {
      stopScrolling();
    }
  }, [placing, startScrolling, stopScrolling]);

  useEffect(() => {
    const { nativeEvent, mouseOnFlow } = stateRef.current;
    // Re-dispatch the mousemove event on the document after the transform occurred:
    // this will allow the step being dragged to have its position updated by
    // react-draggable even when the user's mouse is not moving.
    if (placing && nativeEvent && mouseOnFlow) {
      const { target } = nativeEvent;
      const ownerDocument =
        (target instanceof Node && target.ownerDocument) || target;
      ownerDocument?.dispatchEvent(nativeEvent);
    }
    if (!placing) {
      // Reset some state when placing ends
      Object.assign(stateRef.current, {
        dX: 0,
        dY: 0,
        nativeEvent: null,
        flowElRect: null,
        hasInteractedWithoutScroll: false,
      });
    }
  }, [placing, transform]);

  return {
    // These should be spread into <ReactFlow /> to enable scrolling
    // when the user is placing an item and is near the outer edge of the flow.
    onMouseMove: (ev: MouseEvent) => {
      if (placing) {
        const { nativeEvent, currentTarget: flowEl } = ev;

        stateRef.current.nativeEvent = nativeEvent;
        stateRef.current.flowElRect =
          stateRef.current.flowElRect || flowEl.getBoundingClientRect();

        const { flowElRect } = stateRef.current;
        const mouseCoords = { x: getMouseX(ev), y: getMouseY(ev) };

        stateRef.current.dX = 0;
        stateRef.current.dY = 0;

        if (nearLeft(mouseCoords, flowElRect)) {
          stateRef.current.dX += REFRESH_PX;
        }
        if (nearRight(mouseCoords, flowElRect)) {
          stateRef.current.dX -= REFRESH_PX;
        }
        if (nearTop(mouseCoords, flowElRect)) {
          stateRef.current.dY += REFRESH_PX;
        }
        if (nearBottom(mouseCoords, flowElRect)) {
          stateRef.current.dY -= REFRESH_PX;
        }

        if (stateRef.current.dX === 0 && stateRef.current.dY === 0) {
          stateRef.current.hasInteractedWithoutScroll = true;
        }

        if (!stateRef.current.hasInteractedWithoutScroll) {
          // When the user starts dragging it cannot already be scrolling.
          // This check cancels the scroll until the user has had an interaction
          // that did not result in a scroll.  This occurs e.g. if the user
          // drags a step in from the control bar and immediately triggers nearTop().
          stateRef.current.dX = 0;
          stateRef.current.dY = 0;
        }
      }
    },
    onMouseEnter: () => {
      stateRef.current.mouseOnFlow = true;
    },
    onMouseLeave: () => {
      stateRef.current.mouseOnFlow = false;
    },
  };
}

export const useScrollFlowWhilePlacing = () => {
  const placing = useAutomationsSelector(getPlacing);
  const placingVariable = useAutomationsSelector(getPlacingVariable);
  return useScrollFlowWhilePlacingCore(placing || placingVariable);
};

// Owns the flow's viewport: calculates the translate extent, fits the viewport
// initially, recenters on the focused node and keeps the extent enforced.
// All of it happens in a single effect, as each of those moves the same viewport
// and they have to run in order: fit first, then focus, then extent enforcement.
//
// Calculate the translate extent such that the box containing
// the rendered elements is always visible in the agentic workflow flow.
//
// What is "translate extent"?  It is a concept in d3-zoom and by
// extension, a concept in react-flow-renderer.  This defines the
// coordinates of the full space that needs to be navigable via scrolling.
//
// The requirements are that the agentic workflow flow will be centered on the
// screen, and the screen will not scroll until the top, bottom, left
// or right of the flow are within 100px of the side of the screen.
// As it gets wider than that, a scrollbar will appear.  The agentic workflow
// can be scrolled such that any of its extents may be <=100px from
// the edge of the flow.

export function useTranslateExtent({
  minZoom,
  isEditView = true,
  centerNodeId = 'trigger.junction',
  canvasPadding = CANVAS_PADDING,
  canvasTopPadding = CANVAS_TOP_PADDING,
  // The laid out nodes (see useFlowLayout). react-flow's internal store receives
  // them one effect later, so reading them from there would lag behind the layout.
  nodes,
  stepSlotHeight = STEP_SLOT_HEIGHT.edit,
  // Whether the layout of `nodes` is final
  ready = true,
  // The node to recenter on whenever `focusToken` changes
  focusNodeId = null,
  focusToken = null,
}: {
  minZoom: number;
  isEditView?: boolean;
  centerNodeId?: string;
  canvasPadding?: number;
  canvasTopPadding?: number;
  nodes: any[];
  stepSlotHeight?: StepSlotHeight;
  ready?: boolean;
  focusNodeId?: string | null;
  focusToken?: any;
}) {
  const height = useStore((state) => state.height);
  const width = useStore((state) => state.width);
  const zoom = useStore((state) => state.transform[2]);
  const lastTranslateExtentRef = useRef([
    [0, 0],
    [0, 0],
  ]);
  const [[minX, minY], [maxX, maxY]] = getExtent(nodes, stepSlotHeight);

  // The d3Zoom instance the first transition / offscreen move was done with.
  // React-flow resets its store and creates a new d3Zoom whenever <ReactFlow /> remounts
  // (e.g. switching between the edit and executions views), which needs a fresh first
  // transition even though the laid out nodes (and so the extent) stay the same.
  const firstTransitionZoomRef = useRef<ZoomBehavior<Element, unknown> | null>(
    null
  );
  const viewportFitOffscreenZoomRef = useRef<ZoomBehavior<
    Element,
    unknown
  > | null>(null);

  // The flow remounts when switching views. When it mounts in the edit view a
  // focus that is already in the store was requested for the view switched
  // from, so it's treated as handled and the initial fit recenters the canvas
  // instead. The canvas search re-runs once mounted and requests a fresh focus
  // on its first match, if there's any.
  const handledFocusTokenRef = useRef(isEditView ? focusToken : null);
  const recenteringRef = useRef(false);
  const recenterTokenRef = useRef(0);
  const recenterTargetRef = useRef<Coord | null>(null);

  const [translateExtent, scrollingX, scrollingY] = useMemo(() => {
    // First the x (horizontal) scroll direction:
    // The "extent" is the width of the agentic workflow flow per it caluclated extent
    const extentX = maxX - minX;

    // When the extentX is zero we have do some thing like gone into extendedMode,
    // We need to reset firstTransitionZoomRef so we get the correct first translation
    if (!extentX) {
      firstTransitionZoomRef.current = null;
    }
    // We only scroll when the agentic workflow is wider than the screen, with some play
    const scrollingX = extentX > (width - 2 * canvasPadding) / zoom;
    // At this point we just have to draw some diagrams to determine
    // how to calculate the translate extent in the case of a. scrolling
    // and b. not scrolling in the horizontal direction.
    const [minTranslateX, maxTranslateX] = scrollingX
      ? [minX - canvasPadding / zoom, maxX + canvasPadding / zoom]
      : [
          minX + (extentX - width / zoom) / 2,
          minX + (extentX + width / zoom) / 2,
        ];

    // Now the y (vertical) scroll direction
    // (see analogous comments above for horizontal scroll direction):
    const extentY = maxY - minY;
    const scrollingY =
      extentY > (height - (canvasTopPadding + canvasPadding)) / zoom;

    const [minTranslateY, maxTranslateY] = scrollingY
      ? [minY - canvasTopPadding / zoom, maxY + canvasPadding / zoom]
      : [
          minY + (extentY - height / zoom) / 2,
          minY + (extentY + height / zoom) / 2,
        ];

    const translateExtent: [[number, number], [number, number]] = [
      [minTranslateX, minTranslateY],
      [maxTranslateX, maxTranslateY],
    ];

    return [translateExtent, scrollingX, scrollingY];
  }, [
    height,
    width,
    zoom,
    minX,
    maxX,
    minY,
    maxY,
    canvasPadding,
    canvasTopPadding,
  ]);

  const d3Zoom = useStore((s) => s.d3Zoom);
  const d3Selection = useStore((s) => s.d3Selection);
  const domNode = useStore((s) => s.domNode);

  const { setViewport, getViewport } = useReactFlow();
  const store = useStoreApi();

  useEffect(() => {
    // Right after <ReactFlow /> remounts this can still run with the previous,
    // already detached d3Zoom: wait for the new one.
    if (!d3Zoom || !d3Selection || d3Zoom !== store.getState().d3Zoom) {
      return;
    }

    d3Zoom.interpolate(interpolateLinearly);

    if (!nodes.length || !ready || !width || !height) {
      // Avoid repositioning until the nodes are drawn with their measurements
      // and the (possibly remounted) flow has measured its own dimensions
      if (viewportFitOffscreenZoomRef.current !== d3Zoom && d3Zoom) {
        // one time move the viewport way off screen so that it doesn't Pop
        viewportFitOffscreenZoomRef.current = d3Zoom;
        setViewport({ x: 20000, y: 20000, zoom: 1 });
      }
      return;
    }

    // 1. Initial fit

    if (firstTransitionZoomRef.current !== d3Zoom) {
      // Notes on below: translateBy(0, 0) is a way to get d3Zoom to enforce translateExtent.
      d3Zoom.translateTo(d3Selection, 0, 0); // Scroll all the way to top left.
      d3Zoom.translateBy(d3Selection, 0, 0);
      firstTransitionZoomRef.current = d3Zoom;
      viewportFitOffscreenZoomRef.current = d3Zoom;
      lastTranslateExtentRef.current = translateExtent;

      if (scrollingX || scrollingY) {
        // Calculate the viewport fit for the initial view.
        // We rely on trigger junction as a vertical center
        // 1.1 calculated from:
        // 1.0 is an initial zoom, 0.1 is a padding stolen form d3-zoom fitView
        const xZoom = width / ((maxX - minX) * 1.1);
        const yZoom = height / ((maxY - minY) * 1.1);
        const zoom = clamp(Math.min(xZoom, yZoom), minZoom, 1.0);

        const domRect = domNode?.getBoundingClientRect();
        const verticalCenter = (domRect?.height ?? height) / 2;

        const triggerJunctionNode = nodes.find((n) => n.id === centerNodeId);

        const centerY =
          (triggerJunctionNode?.position?.y ?? 0) +
          (triggerJunctionNode?.height ?? 0) / 2;

        const [[, minTranslateY], [, maxTranslateY]] = translateExtent;
        const minViewportY = height - maxTranslateY * zoom;
        const maxViewportY = -minTranslateY * zoom;

        setViewport({
          x: scrollingX ? canvasPadding - minX * zoom : getViewport().x,
          y: scrollingY
            ? clamp(
                verticalCenter - centerY * zoom,
                Math.min(minViewportY, maxViewportY),
                Math.max(minViewportY, maxViewportY)
              )
            : getViewport().y,
          zoom,
        });
      }
    }

    // 2. Focus. While recentering keep following the target, it moves if e.g.
    // the extent or the flow dimensions settle after switching views.

    const focusPending =
      focusNodeId && focusToken !== handledFocusTokenRef.current;
    const focused =
      (focusPending || recenteringRef.current) &&
      (scrollingX || scrollingY) &&
      nodes.find(({ id }) => id === focusNodeId);

    if (focused) {
      const [[extentMinX, extentMinY], [extentMaxX, extentMaxY]] =
        translateExtent;
      const focusedWidth = focused.width ?? 0;
      const focusedHeight = focused.height ?? 0;

      const extentCoversFocused =
        focused.position.x >= extentMinX &&
        focused.position.x + focusedWidth <= extentMaxX &&
        focused.position.y >= extentMinY &&
        focused.position.y + focusedHeight <= extentMaxY;

      if (extentCoversFocused) {
        // Read live, the initial fit above may have just changed it
        const { x: tX, y: tY, zoom: currentZoom } = getViewport();

        const nodeCenterX = focused.position.x + focusedWidth / 2;
        const nodeCenterY = focused.position.y + focusedHeight / 2;

        const focusedX = width / 2 - nodeCenterX * currentZoom;
        const focusedY = height / 2 - nodeCenterY * currentZoom;

        const tNextX = scrollingX
          ? clamp(
              focusedX,
              width - extentMaxX * currentZoom,
              -extentMinX * currentZoom
            )
          : tX;

        const tNextY = scrollingY
          ? clamp(
              focusedY,
              height - extentMaxY * currentZoom,
              -extentMinY * currentZoom
            )
          : tY;

        const lastTarget = recenterTargetRef.current;
        const alreadyHeadingThere =
          !focusPending &&
          lastTarget &&
          Math.abs(lastTarget.x - tNextX) < 1 &&
          Math.abs(lastTarget.y - tNextY) < 1;

        if (!alreadyHeadingThere) {
          recenteringRef.current = true;
          recenterTargetRef.current = { x: tNextX, y: tNextY };
          handledFocusTokenRef.current = focusToken;

          // Starting a new transition interrupts the previous one, if any
          const recenterToken = ++recenterTokenRef.current;
          d3Selection
            .transition()
            .duration(RECENTER_TRANSITION_MS)
            .call(
              d3Zoom.transform,
              zoomIdentity.translate(tNextX, tNextY).scale(currentZoom)
            )
            .end()
            .catch(() => {})
            .finally(() => {
              if (recenterTokenRef.current === recenterToken) {
                recenteringRef.current = false;
                recenterTargetRef.current = null;
              }
            });
        }
      }
    }

    // 3. Keep the translate extent enforced for later changes, using a quick
    // transition to make the experience less choppy. The recentering target is
    // already within the extent, so the enforcement is skipped while it runs.

    if (!isEqual(translateExtent, lastTranslateExtentRef.current)) {
      lastTranslateExtentRef.current = translateExtent;

      if (!recenteringRef.current) {
        // Avoid kicking off a transition when it shouldn't translate anything
        d3Selection.transition().duration(80).call(d3Zoom.translateBy, 0, 0);
      }
    }
  }, [
    store,
    translateExtent,
    nodes,
    stepSlotHeight,
    ready,
    d3Zoom,
    d3Selection,
    domNode,
    minX,
    minY,
    maxX,
    maxY,
    width,
    height,
    minZoom,
    setViewport,
    isEditView,
    centerNodeId,
    canvasPadding,
    canvasTopPadding,
    scrollingX,
    scrollingY,
    getViewport,
    focusNodeId,
    focusToken,
  ]);

  return { translateExtent, scrollingX, scrollingY };
}
